import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { CompanyParametersQuery, CurrencyCollectionQuery, CurrencyItem, VatRateItem } from '@skylab-shared/business';
import { LocalContext, ModelBase, ObservableCollection } from '@skylab-shared/core';
import { VatTableItemModel } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding } from '@skylab-shared/utils';

import { L10n, L10nBase } from '@skylab-angular-shared/core';
import { CurrencyPipe, TranslatePipe } from '@skylab-angular-shared/pipes';
import { ButtonComponent, DialogContentComponentBase, DialogContentComponentIns, DialogContentViewModel, DialogHeaderComponentIns, DialogInsService, GridInsComponent, ServiceGridColumn, ServiceGridOptions } from '@skylab-angular-shared/ui';

interface DocumentModelPart extends ModelBase {
    currencyId: number;
    vatTableItems: ObservableCollection<VatTableItemModel>;
    vatTableItemsInBaseCurrency: ObservableCollection<VatTableItemModel>;
}

export interface VatTableDialogData {
    readonly context: LocalContext;
    readonly document: DocumentModelPart;
    readonly hideBaseCurrencyVatTable?: boolean;
    readonly vatRates: VatRateItem[];
}

type DocumentVatTableGridColumnIdentity = 'vat-rate' | 'net-value' | 'vat-value' | 'gross-value';

interface VatTableDialogViewModel extends DialogContentViewModel {
    documentInForeignCurrency: boolean;
    hideBaseCurrencyVatTable: boolean;

    currency: CurrencyItem;
    gridContext: {
        currency: CurrencyItem;
    };

    baseCurrency?: CurrencyItem;
    gridContextInBaseCurrency: {
        currency: CurrencyItem;
    };
    isFormReady: boolean;

    gridOptions: ServiceGridOptions<VatTableItem, DocumentVatTableGridColumnIdentity, unknown>;

    gridColumns$: BehaviorSubject<ServiceGridColumn<DocumentVatTableGridColumnIdentity, unknown, VatTableItem, unknown, any>[]>;
    gridColumnsBaseCurrency$: BehaviorSubject<ServiceGridColumn<DocumentVatTableGridColumnIdentity, unknown, VatTableItem, unknown, any>[]>;

    gridDataSource$: BehaviorSubject<VatTableItem[]>;
    gridDataSourceBaseCurrency$: BehaviorSubject<VatTableItem[]>;
}

interface VatTableItem {
    readonly vatRateName: string;
    readonly currencySymbol: string;
    readonly currencyPrecision: number;
    readonly netValue: number;
    readonly vatValue: number;
    readonly grossValue: number;
}

@Component({
    selector: 'skl-document-vat-table-dialog',
    templateUrl: './document-vat-table-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, GridInsComponent, ButtonComponent, TranslatePipe]
})
export class DocumentVatTableDialogComponent extends DialogContentComponentBase<VatTableDialogViewModel, VatTableDialogData> {

    public constructor(
        @Inject(L10n) private readonly l10n: L10nBase,
        private readonly currencyPipe: CurrencyPipe,
        dialogService: DialogInsService,
    ) {
        super(dialogService);
    }

    protected override getInitialState(): VatTableDialogViewModel {
        return {
            ...super.getInitialState(),
            gridOptions: {
                selection: null,
                summary: {isVisible: true},
                stickyHeader: false,
            },

            gridDataSource$: new BehaviorSubject<VatTableItem[]>([]),
            gridDataSourceBaseCurrency$: new BehaviorSubject<VatTableItem[]>([]),

            gridColumns$: new BehaviorSubject(this.getGridColumns('foreign')),
            gridColumnsBaseCurrency$: new BehaviorSubject(this.getGridColumns('base')),

            hideBaseCurrencyVatTable: this.data.hideBaseCurrencyVatTable === true
        };
    }

    protected override async prepareDataAsync(): Promise<void> {
        await Promise.all([
            this.data.context.preload(CompanyParametersQuery),
            this.data.context.preload(CurrencyCollectionQuery)
        ]);

        const inForeginCurrency = (currencyId: number) => {
            if (!currencyId) return false;

            const companyParams = this.data.context.getPreloaded(CompanyParametersQuery);
            const systemCurrency = companyParams?.systemCurrency;
            if (!systemCurrency) return false;
            const preloadedCurrency = this.data.context.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });
            return currencyId !== preloadedCurrency?.id;
        };

        if (inForeginCurrency(this.data.document.currencyId)) {
            this.viewModel.documentInForeignCurrency = true;

            this.prepareViewModelForDocumentInForeignCurrency();
        } else {
            this.viewModel.documentInForeignCurrency = false;

            this.prepareViewModelForDocument();
        }
    }

    private getGridColumns(mode?: 'base' | 'foreign'): ServiceGridColumn<DocumentVatTableGridColumnIdentity, unknown, VatTableItem, unknown, any>[] {
        const getCalculationData = () => {
            const currency = mode === 'base' ? this.viewModel.gridContextInBaseCurrency.currency : this.viewModel.gridContext.currency;
            const collection = mode === 'base' ? this.viewModel.gridDataSourceBaseCurrency$.value : this.viewModel.gridDataSource$.value;
            return { currency, collection };
        };

        return [
            {
                header: this.l10n.instant('scenes.document.form.dialogs.vat-table.grid.columns.vat-rate.title'),
                columnDef: 'vatRateName',
                cell: entity => entity.vatRateName,
                summary: () => this.l10n.instant('scenes.document.form.dialogs.vat-table.grid.summary.text'),
                identityName: 'vat-rate',
                minWidth: '100px',
                isMovable: false,
                isResizable: false
            },
            {
                header: this.l10n.instant('scenes.document.form.dialogs.vat-table.grid.columns.net-value.title'),
                columnDef: 'netValue',
                cell: entity => this.currencyPipe.transform(entity.netValue, { precision: entity.currencyPrecision, symbol: entity.currencySymbol }),
                summary: () => {
                    const { currency, collection } = getCalculationData();
                    return this.currencyPipe.transform(collection.reduce((prev, curr) => Calc.round(prev + curr.netValue, currency.precision, MidpointRounding.AwayFromZero), 0), {...currency});
                },
                identityName: 'net-value',
                styleClass: 'column--align-right',
                width: '160px',
                isMovable: false,
                isResizable: false
            },
            {
                header: this.l10n.instant('scenes.document.form.dialogs.vat-table.grid.columns.vat-value.title'),
                cell: entity => this.currencyPipe.transform(entity.vatValue, { precision: entity.currencyPrecision, symbol: entity.currencySymbol }),
                columnDef: 'vatValue',
                summary: () => {
                    const { currency, collection } = getCalculationData();
                    return this.currencyPipe.transform(collection.reduce((prev, curr) => Calc.round(prev + curr.vatValue, currency.precision, MidpointRounding.AwayFromZero), 0), {...currency});
                },
                identityName: 'vat-value',
                styleClass: 'column--align-right',
                width: '160px',
                isMovable: false,
                isResizable: false
            },
            {
                header: this.l10n.instant('scenes.document.form.dialogs.vat-table.grid.columns.gross-value.title'),
                cell: entity => this.currencyPipe.transform(entity.grossValue, { precision: entity.currencyPrecision, symbol: entity.currencySymbol }),
                columnDef: 'grossValue',
                summary: () => {
                    const { currency, collection } = getCalculationData();
                    return this.currencyPipe.transform(collection.reduce((prev, curr) => Calc.round(prev + curr.grossValue, currency.precision, MidpointRounding.AwayFromZero), 0), {...currency});
                },
                identityName: 'gross-value',
                width: '204px',
                styleClass: 'column--align-right',
                isMovable: false,
                isResizable: false
            }
        ];
    }

    private prepareViewModelForDocumentInForeignCurrency(): void {
        this.prepareViewModelForDocument();

        const companyParams = this.data.context.getPreloaded(CompanyParametersQuery);
        const systemCurrency = companyParams?.systemCurrency;
        if (!systemCurrency) return;
        const baseCurrency = this.data.context.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });
        if (!baseCurrency) return;
        this.viewModel.baseCurrency = baseCurrency;
        this.viewModel.gridDataSourceBaseCurrency$.next(this.prepareVatTableItems(this.data.document.vatTableItemsInBaseCurrency, baseCurrency));
        this.viewModel.gridContextInBaseCurrency = { currency: baseCurrency };
    }

    private prepareViewModelForDocument(): void {
        const currency = this.data.context.firstPreloaded(CurrencyCollectionQuery, { id: this.data.document.currencyId });
        if (!currency) return;
        this.viewModel.currency = currency;
        this.viewModel.gridDataSource$.next(this.prepareVatTableItems(this.data.document.vatTableItems, currency));
        this.viewModel.gridContext = { currency };
    }

    private prepareVatTableItems(items: ObservableCollection<VatTableItemModel>, currency: CurrencyItem): VatTableItem[] {
        return items.sort((a, b) => {
            const aVatRate = this.data.vatRates.find(elem => elem.id === a.vatRateId);
            const bVatRate = this.data.vatRates.find(elem => elem.id === b.vatRateId);
            const aOrder = aVatRate?.order ?? 0;
            const bOrder = bVatRate?.order ?? 0;
            return aOrder > bOrder ? 1 : -1;
        }).map<VatTableItem>(item => {
            const vatRate = this.data.vatRates.find(elem => elem.id === item.vatRateId);
            return {
                vatRateName: vatRate?.name ?? '',
                currencySymbol: currency.symbol,
                currencyPrecision: currency.precision,
                netValue: item.netValue,
                vatValue: item.vatValue,
                grossValue: item.grossValue
            };
        });
    }

    protected override isReady(): void {
        this.viewModel.isFormReady = true;
    }
}
